import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TableSelectionModalData, TableSelectionModalResult, TableHeaderConfig, TableColorConfig } from './table-selection-modal.model';
import { DatasetOption } from '../dataset-option.model';
import { MetricDto } from 'src/app/services/models/metric.models';

@Component({
  selector: 'bhub-table-selection-modal',
  templateUrl: './table-selection-modal.component.html',
  styleUrls: ['./table-selection-modal.component.scss'],
  standalone: false
})
export class TableSelectionModalComponent implements OnInit {
  headerOrientation: 'horizontal' | 'vertical' = 'horizontal';
  selectedDataset: DatasetOption | null = null;
  selectedField: any = null;
  headers: TableHeaderConfig[] = [];
  groupByFieldCustomName: string = '';
  colorConfig: TableColorConfig = this.getDefaultColorConfig();

  datasetFields: any[] = [];

  private getDefaultColorConfig(): TableColorConfig {
    return {
      headerBackgroundColor: '#e0e0e0', // Light grey
      headerTextColor: '#000000', // Black
      groupingBackgroundColor: '#e6e6e6', // Lighter grey
      groupingTextColor: '#000000', // Black
      dataBackgroundColor1: '#ffffff', // White
      dataBackgroundColor2: '#f0f8ff', // Very light blue
      dataTextColor: '#000000' // Black
    };
  }

  constructor(
    public dialogRef: MatDialogRef<TableSelectionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TableSelectionModalData
  ) {}

  ngOnInit() {
    this.initializeHeaders();

    if (this.data.initialValue) {
      this.headerOrientation = this.data.initialValue.headerOrientation;
      this.headers = [...this.data.initialValue.headers];
      this.groupByFieldCustomName = this.data.initialValue.groupByFieldCustomName || '';
      this.colorConfig = this.data.initialValue.colorConfig || this.getDefaultColorConfig();

      // Find and set the selected dataset and field
      const dataset = this.data.availableDatasets.find(d =>
        d.datasetId?.toString() === this.data.initialValue?.groupByDataset
      );
      if (dataset) {
        this.selectedDataset = dataset;
        this.loadDatasetFields();
        // Set selected field after fields are loaded
        setTimeout(() => {
          this.selectedField = this.datasetFields.find(f =>
            f.fieldId === this.data.initialValue?.groupByField
          );
        }, 100);
      }
    }
  }

  initializeHeaders() {
    const headerCount = this.headerOrientation === 'horizontal'
      ? this.data.columns - 1  // First column is for dimension values
      : this.data.rows - 1;    // First row is for dimension values

    this.headers = [];
    for (let i = 0; i < headerCount; i++) {
      this.headers.push({
        index: i,
        type: 'metric',
        displayName: `Header ${i + 1}`,
        metricId: undefined,
        metricName: undefined
      });
    }
  }

  onOrientationChange() {
    this.initializeHeaders();
  }

  onDatasetSelect() {
    if (this.selectedDataset) {
      this.loadDatasetFields();
      this.selectedField = null;
      this.groupByFieldCustomName = '';
    }
  }

  onFieldSelect() {
    if (this.selectedField) {
      // Set default custom name to field name if not already set
      if (!this.groupByFieldCustomName) {
        this.groupByFieldCustomName = this.selectedField.fieldName;
      }
    }
  }

  loadDatasetFields() {
    if (!this.selectedDataset) {
      this.datasetFields = [];
      return;
    }

    // Extract fields from the dataset's dataTableSchemas
    const fullDataset = this.selectedDataset._fullDataset;
    if (fullDataset && fullDataset.dataTableSchemas) {
      this.datasetFields = fullDataset.dataTableSchemas
        .filter((schema: any) => !schema.isHidden) // Only show non-hidden fields
        .map((schema: any) => ({
          fieldId: schema.fieldId.toString(),
          fieldName: schema.displayName || schema.fieldName,
          dataType: schema.dataType,
          description: schema.description,
          isPrimaryKey: schema.isPrimaryKey,
        }));

    } else {
      console.warn('No dataTableSchemas found for dataset:', this.selectedDataset);
      this.datasetFields = [];
    }
  }

  onHeaderTypeChange(header: TableHeaderConfig) {
    // Reset the header when type changes
    if (header.type === 'dimension') {
      header.metricId = undefined;
      header.metricName = undefined;
      header.datasetId = undefined;
      header.fieldId = undefined;
      header.fieldName = undefined;
    } else {
      header.datasetId = undefined;
      header.fieldId = undefined;
      header.fieldName = undefined;
      header.metricId = undefined;
      header.metricName = undefined;
    }
  }

  onMetricSelect(header: TableHeaderConfig, metric: MetricDto) {
    if (metric) {
      header.metricId = metric.metricId.toString();
      header.metricName = metric.metricName;
      header.displayName = metric.metricName;
      // Set default custom name to metric name if not already set
      if (!header.customName) {
        header.customName = metric.metricName;
      }
    }
  }

  getSelectedMetric(header: TableHeaderConfig): MetricDto | undefined {
    if (!header.metricId) return undefined;
    return this.data.availableMetrics.find(m => m.metricId.toString() === header.metricId);
  }

  getGridDimensions(): { rows: number, columns: number } {
    return {
      rows: this.data.rows,
      columns: this.data.columns
    };
  }

  getHeaderCount(): number {
    return this.headerOrientation === 'horizontal'
      ? this.data.columns - 1
      : this.data.rows - 1;
  }

  getPreviewTableStructure(): any {
    const structure = {
      headers: [],
      dataRows: []
    };

    if (this.headerOrientation === 'horizontal') {
      // First cell is the dimension name, then metric headers
      structure.headers = [
        this.groupByFieldCustomName || this.selectedField?.fieldName || 'Dimension',
        ...this.headers.map(h => h.customName || h.displayName)
      ];

      // Add sample data rows
      const dataRowCount = Math.min(3, this.data.rows - 1);
      for (let i = 0; i < dataRowCount; i++) {
        const row = ['Value ' + (i + 1)];
        this.headers.forEach(h => {
          row.push(h.type === 'metric' ? '123.45' : 'Data');
        });
        structure.dataRows.push(row);
      }
    } else {
      // Vertical orientation - headers on left
      structure.headers = ['Field', 'Value 1', 'Value 2', 'Value 3'];

      // First row is the dimension
      structure.dataRows.push([
        this.groupByFieldCustomName || this.selectedField?.fieldName || 'Dimension',
        'Item 1', 'Item 2', 'Item 3'
      ]);

      // Then metric rows
      this.headers.forEach(h => {
        structure.dataRows.push([
          h.customName || h.displayName,
          h.type === 'metric' ? '123.45' : 'Data 1',
          h.type === 'metric' ? '456.78' : 'Data 2',
          h.type === 'metric' ? '789.01' : 'Data 3'
        ]);
      });
    }

    return structure;
  }

  isValidSelection(): boolean {
    return this.selectedDataset !== null &&
           this.selectedField !== null &&
           this.headers.every(h => h.type !== 'metric' || h.metricId);
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (!this.isValidSelection()) return;

    const result: TableSelectionModalResult = {
      headerOrientation: this.headerOrientation,
      groupByDataset: this.selectedDataset!.datasetId!.toString(),
      groupByField: this.selectedField.fieldId,
      groupByFieldName: this.selectedField.fieldName,
      groupByFieldCustomName: this.groupByFieldCustomName || this.selectedField.fieldName,
      headers: this.headers,
      colorConfig: this.colorConfig,
      selectedRows: this.data.rows,
      selectedColumns: this.data.columns
    };

    this.dialogRef.close(result);
  }

  getPreviewCellStyle(rowIndex: number, cellIndex: number): any {
    // Check if this is a grouping cell (first column/row)
    const isGroupingCell = (this.headerOrientation === 'horizontal' && cellIndex === 0) ||
                          (this.headerOrientation === 'vertical' && rowIndex === 0);

    if (isGroupingCell) {
      return {
        'background-color': this.colorConfig.groupingBackgroundColor,
        'color': this.colorConfig.groupingTextColor
      };
    } else {
      // Alternating data cell colors
      const isAlternating = (this.headerOrientation === 'horizontal' ? rowIndex : cellIndex) % 2 === 1;
      return {
        'background-color': isAlternating ? this.colorConfig.dataBackgroundColor2 : this.colorConfig.dataBackgroundColor1,
        'color': this.colorConfig.dataTextColor
      };
    }
  }
}
